/**
 * ChatBubble — renders a single chat message with delivery status and rich blocks.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { LinkifiedText } from "../LinkifiedText";

import type {
    ChatMessage,
    ChatResponseBlock,
    DeliveryStatus,
    QuickReplyOption,
    VisitCardData
} from "@/lib/chat-api";
import {
    colors,
    fonts,
    radius,
    spacing,
    typography,
    statusColors,
    statusLabels
} from "@/lib/theme";

interface ChatBubbleProps {
    message: ChatMessage;
    deliveryStatus?: DeliveryStatus;
    onChipTap?: (value: string) => void;
    onRetry?: () => void;
}

export function ChatBubble({ message, deliveryStatus, onChipTap, onRetry }: ChatBubbleProps) {
    const isRN = message.role === "rn";
    const blocks = (message.responseBlocks as ChatResponseBlock[]) ?? [];
    const showBlocks = !isRN && blocks.length > 0;

    const timestamp = message.createdAt
        ? new Date(message.createdAt).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit"
          })
        : null;

    return (
        <View style={[styles.container, isRN ? styles.rnContainer : styles.botContainer]}>
            {/* Sender label for non-RN */}
            {!isRN && message.senderName && (
                <Text style={styles.senderName}>{message.senderName}</Text>
            )}

            {/* Main bubble */}
            <View style={[styles.bubble, isRN ? styles.rnBubble : styles.botBubble]}>
                {showBlocks
                    ? blocks.map((block, i) => (
                          <RenderBlock key={i} block={block} onChipTap={onChipTap} />
                      ))
                    : message.content && (
                          <LinkifiedText
                              style={[styles.messageText, isRN && styles.rnText]}
                              linkColor={isRN ? "#FFFFFF" : colors.teal600}
                          >
                              {message.content}
                          </LinkifiedText>
                      )}
            </View>

            {/* Timestamp + delivery status */}
            <View style={[styles.metaRow, isRN && styles.metaRowRN]}>
                {timestamp && <Text style={styles.timestamp}>{timestamp}</Text>}
                {isRN && <DeliveryIcon status={deliveryStatus ?? "sent"} onRetry={onRetry} />}
            </View>
        </View>
    );
}

function RenderBlock({
    block,
    onChipTap
}: {
    block: ChatResponseBlock;
    onChipTap?: (value: string) => void;
}) {
    switch (block.type) {
        case "text":
            return (
                <LinkifiedText style={styles.messageText} linkColor={colors.teal600}>
                    {block.text}
                </LinkifiedText>
            );

        case "quick_replies":
            return (
                <View style={styles.chipsContainer}>
                    {block.options.map((opt: QuickReplyOption, i: number) => (
                        <TouchableOpacity
                            key={i}
                            style={styles.chip}
                            onPress={() => onChipTap?.(opt.value)}
                            activeOpacity={0.7}
                        >
                            {opt.icon && (
                                <MaterialCommunityIcons
                                    name={opt.icon as keyof typeof MaterialCommunityIcons.glyphMap}
                                    size={16}
                                    color={colors.teal400}
                                />
                            )}
                            <Text style={styles.chipText}>{opt.label}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
            );

        case "visit_cards":
            return (
                <View style={styles.cardsContainer}>
                    {block.visits.map((visit: VisitCardData) => (
                        <TouchableOpacity
                            key={visit.id}
                            style={styles.visitCard}
                            onPress={() => onChipTap?.(visit.id)}
                            activeOpacity={0.7}
                        >
                            <View style={styles.visitCardHeader}>
                                <Text style={styles.visitPatientName}>{visit.patientName}</Text>
                                <View
                                    style={[
                                        styles.statusBadge,
                                        {
                                            backgroundColor:
                                                statusColors[visit.status] ?? colors.info500
                                        }
                                    ]}
                                >
                                    <Text style={styles.statusBadgeText}>
                                        {statusLabels[visit.status] ?? visit.status}
                                    </Text>
                                </View>
                            </View>
                            {visit.time ? <Text style={styles.visitTime}>{visit.time}</Text> : null}
                            {visit.address ? (
                                <Text style={styles.visitAddress} numberOfLines={1}>
                                    {visit.address}
                                </Text>
                            ) : null}
                        </TouchableOpacity>
                    ))}
                </View>
            );

        case "action_confirm":
            return (
                <View style={styles.actionConfirm}>
                    <Text style={styles.actionLabel}>{block.action.label}</Text>
                    <Text style={styles.actionDesc}>{block.action.description}</Text>
                </View>
            );

        default:
            return null;
    }
}

function DeliveryIcon({ status, onRetry }: { status: DeliveryStatus; onRetry?: () => void }) {
    switch (status) {
        case "sending":
            return (
                <MaterialCommunityIcons
                    name="clock-outline"
                    size={14}
                    color={colors.textTertiary}
                />
            );
        case "sent":
            return <MaterialCommunityIcons name="check" size={14} color={colors.textTertiary} />;
        case "delivered":
            return <MaterialCommunityIcons name="check-all" size={14} color={colors.teal400} />;
        case "failed":
            return (
                <TouchableOpacity onPress={onRetry} style={styles.retryBtn}>
                    <MaterialCommunityIcons name="alert-circle" size={14} color={colors.error500} />
                    <Text style={styles.retryText}>Tap to retry</Text>
                </TouchableOpacity>
            );
    }
}

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs
    },
    rnContainer: {
        alignItems: "flex-end"
    },
    botContainer: {
        alignItems: "flex-start"
    },
    senderName: {
        ...typography.label,
        color: colors.textTertiary,
        marginBottom: 2,
        marginLeft: spacing.xs
    },
    bubble: {
        maxWidth: "85%",
        padding: spacing.md
    },
    rnBubble: {
        backgroundColor: colors.teal400,
        borderRadius: 18,
        borderBottomRightRadius: 4
    },
    botBubble: {
        backgroundColor: colors.gray100,
        borderRadius: 18,
        borderBottomLeftRadius: 4
    },
    messageText: {
        ...typography.body,
        color: colors.textPrimary
    },
    rnText: {
        color: "#FFFFFF"
    },

    // ── Meta row (timestamp + delivery) ──
    metaRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginTop: 2,
        marginLeft: spacing.xs
    },
    metaRowRN: {
        justifyContent: "flex-end",
        marginRight: spacing.xs,
        marginLeft: 0
    },
    timestamp: {
        ...typography.label,
        fontSize: 10,
        color: colors.textTertiary
    },

    // ── Delivery ──
    retryBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    retryText: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.error500
    },

    // ── Quick Replies ──
    chipsContainer: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm,
        marginTop: spacing.sm
    },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        backgroundColor: colors.teal50,
        borderRadius: radius.full,
        paddingHorizontal: 14,
        paddingVertical: spacing.sm,
        borderWidth: 1.5,
        borderColor: colors.teal400
    },
    chipText: {
        ...typography.bodySmall,
        fontFamily: fonts.semiBold,
        color: colors.teal400
    },

    // ── Visit Cards ──
    cardsContainer: {
        gap: spacing.sm,
        marginTop: spacing.sm,
        width: "100%"
    },
    visitCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        padding: spacing.md,
        borderWidth: 1.5,
        borderColor: colors.border
    },
    visitCardHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    },
    visitPatientName: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary,
        flex: 1
    },
    statusBadge: {
        borderRadius: radius.sm,
        paddingHorizontal: spacing.sm,
        paddingVertical: 2
    },
    statusBadgeText: {
        ...typography.label,
        color: "#FFFFFF"
    },
    visitTime: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: spacing.xs
    },
    visitAddress: {
        ...typography.caption,
        color: colors.textTertiary,
        marginTop: 2
    },

    // ── Action Confirm ──
    actionConfirm: {
        marginTop: spacing.sm,
        padding: spacing.sm,
        backgroundColor: colors.warning50,
        borderRadius: radius.sm
    },
    actionLabel: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    actionDesc: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        marginTop: spacing.xs
    }
});
